import * as React from "react";
import { ChevronDown, ChevronUp, Clock, Server } from "react-feather";
import { ProcessedInstance, INSTANCE_STATUS_CONFIG, WELL_KNOWN_ENV_KEYS } from "./instanceUtils";
import { CopyButtonWithApproval } from "../../domain/create_ng/copyButtonWithApproval";

interface InstanceCardProps {
  instance: ProcessedInstance;
  isExpanded: boolean;
  onToggle: () => void;
}

export const InstanceCard: React.FC<InstanceCardProps> = ({ instance, isExpanded, onToggle }) => {
  const { info, status, lastActiveText, lastActiveAbsolute } = instance;
  const config = INSTANCE_STATUS_CONFIG[status];
  const StatusIcon = config.icon;
  const env = info.env || {};
  const envKeys = Object.keys(env);

  const wellKnownFields = WELL_KNOWN_ENV_KEYS.filter((wk) => env[wk.key] !== undefined);

  const remainingKeys = envKeys.filter((k) => !WELL_KNOWN_ENV_KEYS.some((wk) => wk.key === k));

  const formatImageValue = (value: string): string => {
    if (!value || !value.includes("/")) return value;
    const parts = value.split("/");
    return parts[parts.length - 1];
  };

  return (
    <div
      className="rounded-lg overflow-hidden"
      style={{
        backgroundColor: "var(--health-card-bg, var(--color-bg-secondary, var(--color-bg)))",
        border: `1px solid var(--color-border)`,
        gridColumn: isExpanded ? "1 / -1" : undefined,
      }}
    >
      {/* Clickable header area */}
      <div className="cursor-pointer" onClick={onToggle} style={{ userSelect: "none", padding: "16px 20px" }}>
        {/* Header row */}
        <div className="flex items-center gap-3 mb-3">
          <div className="flex-shrink-0">
            <StatusIcon size={18} color={config.color} />
          </div>
          <div
            className="truncate min-w-0 flex-1 font-medium"
            style={{ color: "var(--color-default)", fontFamily: "input-mono-compressed, monospace", fontSize: 13 }}
            title={info.instanceId}
          >
            {info.instanceId || "Unknown"}
          </div>
          <div className="flex-shrink-0" style={{ color: "var(--color-label)" }}>
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        </div>

        {/* Status badge + last active */}
        <div className="flex items-center gap-3 mb-4">
          <span
            className="text-xs px-2 py-1 rounded-full"
            style={{ backgroundColor: config.bg, color: config.color }}
          >
            {config.label}
          </span>
          <div
            className="flex items-center gap-1 text-xs"
            style={{ color: "var(--color-label)" }}
            title={lastActiveAbsolute}
          >
            <Clock size={12} />
            <span>{lastActiveText}</span>
          </div>
        </div>

        {/* Well-known env fields */}
        {wellKnownFields.length > 0 && (
          <div className="flex flex-wrap mb-2" style={{ gap: "8px 20px" }}>
            {wellKnownFields.map((wk) => {
              const value = String(env[wk.key]);
              const displayValue = wk.key === "image" ? formatImageValue(value) : value;
              return (
                <div key={wk.key} className="flex items-center gap-1.5 text-xs min-w-0" title={value}>
                  <span style={{ color: "var(--color-label)" }}>{wk.label}:</span>
                  <span
                    className="truncate"
                    style={{
                      color: "var(--color-default)",
                      fontFamily: "input-mono-compressed, monospace",
                      maxWidth: 220,
                    }}
                  >
                    {displayValue}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Peer count */}
        {info.peerCount !== undefined && info.peerCount >= 0 && (
          <div className="flex items-center gap-3 text-xs mt-3" style={{ color: "var(--color-label)" }}>
            <div className="flex items-center gap-1.5">
              <Server size={12} />
              <span>
                {info.peerCount} {info.peerCount === 1 ? "peer" : "peers"}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Expanded section */}
      {isExpanded && (
        <div style={{ borderTop: "1px dashed var(--color-border)", padding: "14px 20px 16px" }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium" style={{ color: "var(--color-label)" }}>
              All Environment Variables ({envKeys.length})
            </span>
            <CopyButtonWithApproval value={JSON.stringify(info, null, 2)} />
          </div>
          {envKeys.length > 0 ? (
            <div className="flex flex-col gap-1.5">
              {envKeys.map((key) => {
                const value = String(env[key]);
                return (
                  <div key={key} className="flex text-xs min-w-0 gap-3">
                    <span className="flex-shrink-0" style={{ color: "var(--color-label)", minWidth: 140 }}>
                      {key}
                    </span>
                    <span
                      className="truncate"
                      style={{
                        color: "var(--color-default)",
                        fontFamily: "input-mono-compressed, monospace",
                      }}
                      title={value}
                    >
                      {value}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <span className="text-xs" style={{ color: "var(--color-label)" }}>
              No environment variables
            </span>
          )}
        </div>
      )}
    </div>
  );
};
